/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { Button } from '@mui/material';
import { DataType, toggleClass } from '@grapecity/wijmo';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import {
  getCommonCodeGroups,
  getCommonCodeNamesCondition,
  getCommonCodes,
  setCommonCodeGroups,
  setCommonCodes,
} from 'apis/admin/CommonCode';
import { CommonCode, CommonCodeGroup } from 'models/admin/CommonCode';
import { CommonYN } from 'models/common/Common';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { CrudCode } from 'models/common/Edit';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { IconButton } from 'components/buttons/IconSVG';
import { commonYNList } from 'models/common/CommonCode';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { useMessageModalStore } from 'stores/useMessageModalStore';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { getExcelFileName } from 'utils/ExcelUtil';
import _, { find } from 'lodash';

interface Props {
  hasAuth?: boolean;
  onRefresh: () => void;
  gridRefresh?: boolean;
}

const MpCodeLv2Grid = ({ hasAuth, onRefresh, gridRefresh }: Props) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const [flexRef, setflexRef] = useState<any>();
  const [flexItem, setFlexItem] = useState<any>();
  const [rowData, setRowData] = useState<CommonCode[]>([]);
  const [mpImprTypeLv1Code, setMpImprTypeLv1Code] = useState<CommonCode[]>([]);
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const { setMessageModalStateWhenModalOpen } = useMessageModalStore();

  useEffect(() => {
    getCommonCode();
  }, [gridRefresh]);

  const getCommonCode = async () => {
    const mpImprTypeLv1: CommonCode[] = await getCommonCodes('MP_IMPR_TYPE_LVL1'); // MP 분류
    const mpImprTypeLv2: CommonCode[] = await getCommonCodes('MP_IMPR_TYPE_LVL2'); // MP 분류
    setRowData(mpImprTypeLv2);
    setMpImprTypeLv1Code(mpImprTypeLv1);
  };

  const layoutDefinition = useMemo(() => {
    const mpImprTypeLv1DataMap = new DataMap(mpImprTypeLv1Code, 'cmnCd', 'cmnCdNm');
    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        width: 45,
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'cmnGrCd',
        visible: false,
      },
      {
        binding: 'optValCtn1',
        header: String(t('com.label.개선목적 Lv1', '개선목적 Lv1')),
        width: 160,
        align: 'center',
        cssClass: 'WijmoSelect',
        dataMap: mpImprTypeLv1DataMap,
        cellTemplate: (item) => {
          if (item.item.crudKey === CrudCode.CREATE) {
            if (mpImprTypeLv1DataMap.getDisplayValue(item.value) == undefined) {
              return `<span>${item.text}</span>`;
            } else {
              return `<span>${mpImprTypeLv1DataMap.getDisplayValue(item.text)}</span>`;
            }
          } else {
            return `<span>${mpImprTypeLv1DataMap.getDisplayValue(item.text)}</span>`;
          }
        },
      },
      {
        header: String(t('admin.grid.코드', '코드')),
        binding: 'cmnCd',
        align: 'center',
        width: 120,
        isReadOnly: true,
      },
      {
        header: String(t('admin.grid.개선목적명', '개선목적명')),
        binding: 'cmnCdNm',
        minWidth: 150,
        width: '*',
        align: 'left',
        cssClass: 'WijmoPopup',
        isReadOnly: !hasAuth,
        cellTemplate: (params) =>
          params.item['crudKey'] === CrudCode.CREATE
            ? params.value
            : `<span>${params.value}</span><Button id="${hasAuth ? 'btnCode' : ''}" />`,
      },
      {
        binding: 'msgCtn',
        visible: false,
      },
      {
        header: String(t('admin.grid.순서', '순서')),
        binding: 'sortOrd',
        dataType: DataType.Number,
        width: 80,
        align: 'center',
        isReadOnly: !hasAuth,
      },
      {
        header: String(t('com.label.사용여부', '사용여부')),
        binding: 'useYn',
        width: 80,
        align: 'center',
        dataMap: new DataMap(commonYNList, 'cmnCd', 'cmnCdNm'),
        isReadOnly: !hasAuth,
      },
    ];
  }, [rowData]);

  const btnExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = '개선목적Lv2';
    book.saveAsync(getExcelFileName(t('admin.label.MP개선목적Lv2', 'MP개선목적Lv2')));
  };

  const beginningEdit = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
    setFlexItem(item);

    if (item.crudKey !== CrudCode.CREATE) {
      if (['optValCtn1', 'cmnCd'].includes(binding)) {
        e.cancel = true;
      }
    }
  });

  const cellEditEnded = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
    if (item.crudKey === CrudCode.CREATE && binding === 'optValCtn1') {
      item.cmnCd = '';
      const selectLv1cmnCd = rowData.filter((data) => data.optValCtn1 === item.optValCtn1);
      const cmnCd = selectLv1cmnCd
        .map((data) => Number(data.cmnCd?.match(/\d+$/)?.at(0)))
        .filter((data) => data !== undefined && data !== null && !Number.isNaN(data))
        .sort((a, b) => a - b);

      let index = 0;
      const findCmnCdNumber = cmnCd.find((currentValue, i) => {
        const missingNumber = currentValue !== i + 1;
        if (missingNumber) {
          index = i + 1;
        }
        return missingNumber;
      });

      const maxCmnCd = findCmnCdNumber ? String(index) : String(Number(cmnCd.pop()) + Number(1));

      item.cmnCd = item.optValCtn1 ? item.optValCtn1 + '-' + maxCmnCd : '';

      gridRef.current.refresh();
    }
  });

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;

      if (item.crudKey !== CrudCode.CREATE) {
        if (['optValCtn1', 'cmnCd'].includes(binding)) {
          cell.ariaReadOnly = true;
          if (cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
    }
  });

  const onInitialized = (grid) => {
    setflexRef(grid);
    gridRef.current = grid;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;

      if (e.target instanceof HTMLButtonElement) {
        switch (e.target.id) {
          // 그룹코드
          case 'btnCode':
            setMessageModalStateWhenModalOpen(item.msgCtn, item.cmnCdNm, (cmnCdNm?: string) => {
              if (cmnCdNm && item) {
                setCommonCodeGroups([{ ...item, cmnCdNm: cmnCdNm, crudKey: CrudCode.UPDATE }]).then(
                  (result) => result && result && getCommonCode()
                );
              }
              openMessageBar({
                type: 'confirm',
                content: t('com.label.저장되었습니다.', '저장되었습니다.'),
              });
            });
        }
      }
      // if (e.target instanceof HTMLInputElement && e.target.type === 'checkbox') {
      //   if (e.srcElement.checked === true) {
      //     setMpLv2CmnCd(item.cmnCd);
      //   }
      // }
    });
  };

  const fnSearchCodeGroup = (searchGrCd: string, searchUseYn: string, searchCode: string) => {
    getCommonCodeGroups(searchGrCd, searchUseYn, searchCode).then((result: CommonCodeGroup[]) => {
      setRowData(result);
    });
  };

  const btnAddRow = useCallback(() => {
    const newRow = {
      crudKey: CrudCode.CREATE,
      cmnGrCd: 'MP_IMPR_TYPE_LVL2',
      useYn: CommonYN.Y,
    } as CommonCode;

    setRowData([newRow, ...rowData]);
  }, [rowData]);

  const btnDelRow = useCallback(() => {
    const isSelected = gridRef.current.rows.filter((item) => item.isSelected);

    if (isSelected.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('mp.label.삭제할 행을 선택해 주세요.', '삭제할 행을 선택해 주세요.'),
      });
      return false;
    } else {
      if (isSelected.length < 1) return;
      isSelected.forEach((row) => {
        const item = row.dataItem;
        if (item.crudKey === CrudCode.CREATE) {
          gridRef.current.collectionView.remove(item);
        } else {
          item.crudKey = CrudCode.UPDATE;
          item.useYn = 'N';
        }
      });
      gridRef.current.refresh();
    }
  }, [rowData]);

  const btnSave = () => {
    const valid = rowData
      .map((rowNode, index) => {
        if (rowNode.optValCtn1 == null || rowNode.optValCtn1 == '')
          return `${index + 1} : ${t(
            'admin.msg.개선목적Lv1을 선택해주세요.',
            '개선목적Lv1을 선택해주세요.'
          )}`;
        if (rowNode.cmnCd == null || rowNode.cmnCd == '')
          return `${index + 1} : ${t('admin.msg.코드를 입력해 주세요.', '코드를 입력해 주세요.')}`;
        if (rowNode.cmnCdNm == null || rowNode.cmnCdNm == '')
          return `${index + 1} : ${t(
            'admin.msg.코드명을 입력해 주세요.',
            '코드명을 입력해 주세요.'
          )}`;
        if (rowNode.sortOrd == null || rowNode.sortOrd == '') rowNode.sortOrd = '0';
      })
      .filter((element) => element !== undefined);
    if (valid?.length) {
      openMessageBar({ type: 'error', content: valid.toString() });
      return false;
    }

    const saveRows = rowData
      .map((rowNode) => {
        return rowNode.crudKey ? rowNode : null;
      })
      .filter((element) => element !== null) as CommonCode[];

    if (saveRows?.length == 0) {
      openMessageBar({
        type: 'error',
        content: t('com.label.수정된 항목이 없습니다.', '수정된 항목이 없습니다.'),
      });
      return true;
    }
    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        setCommonCodes(saveRows).then((result) => {
          if (result.data.type === 'error') {
            openMessageBar({
              type: 'error',
              content:
                'Code: ' +
                result.data.code +
                ' ' +
                t('com.label.중복된 코드가 존재합니다.', '중복된 코드가 존재합니다.'),
            });
            return false;
          }
          if (result.data.type === 'used') {
            openMessageBar({
              type: 'error',
              content:
                'Code: ' +
                result.data.code +
                ' ' +
                t(
                  'com.label.사용중인 코드입니다. 삭제할 수 없습니다.',
                  '사용중인 코드입니다. 삭제할 수 없습니다.'
                ),
            });
            return false;
          }
          if (result.data.type === 'confirm') {
            openMessageBar({
              type: 'confirm',
              content: t('com.label.저장되었습니다.', '저장되었습니다.'),
            });
          }
          gridRef.current.refresh();
          onRefresh();
          getCommonCode();
          // fnSearchCodeGroup(props.sGrCd || '', props.sUseYn || '', props.sCode || '');
        });
      },
      noCallback: () => {
        return false;
      },
    });
  };

  const onCellClicked = (params: any) => {
    // props.callBack && params.crudKey != CrudCode.CREATE && props.callBack(params.cmnGrCd);
  };

  // useEffect(() => {
  //   if (props.searchParamData != false) {
  //     fnSearchCodeGroup(props.sGrCd || '', props.sUseYn || '', props.sCode || '');
  //   }
  // }, [props.sGrCd, props.sUseYn, props.sCode, props.searchParamData, props.searchState]);

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('admin.label.개선목적 Level2', '개선목적 Level2')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {hasAuth && (
            <Button css={IconButton.button} className="addRow" disableRipple onClick={btnAddRow}>
              {t('com.button.행추가', '행추가')}
            </Button>
          )}
          {hasAuth && (
            <Button css={IconButton.button} className="delRow" disableRipple onClick={btnDelRow}>
              {t('com.button.행삭제', '행삭제')}
            </Button>
          )}
          <Button css={IconButton.button} className="refresh" onClick={getCommonCode} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={230}
        deferResizing={false}
        initialized={onInitialized}
        align={'center'}
        isSelector={true}
        isFilter={true}
        allowPinning={false}
        beginningEdit={beginningEdit}
        itemFormatter={onItemFormatter}
        cellEditEnded={cellEditEnded}
      />
      <GlobalBtnGroup>
        {hasAuth && (
          <Button css={IconButton.button} className="save" disableRipple onClick={btnSave}>
            {t('com.button.저장', '저장')}
          </Button>
        )}
      </GlobalBtnGroup>
    </>
  );
};

export default MpCodeLv2Grid;
